import { useLoaderData } from "react-router";

function pluralCredits(rate) {
  return `${rate} ${rate === 1 ? "credit" : "credits"}`;
}

function buildServices(traceRates) {
  return [
    {
      name: "Skip Tracing",
      eyebrow: "Property owner lookup",
      tag: "Most common",
      modes: [
        { name: "Standard", rate: traceRates.normal },
        { name: "Enhanced", rate: traceRates.enhanced },
      ],
      unit: "per record",
      description:
        "Find verified owner contact data from parcel lists, MLS exports, or one-off property searches. Enhanced returns more data per row.",
      features: [
        "Bulk parcel uploads",
        "Owner contact matching",
        "Phone and email enrichment",
      ],
    },
    {
      name: "LLC Skip Tracing",
      eyebrow: "Entity owner lookup",
      credits: pluralCredits(traceRates.llc),
      unit: "per record",
      description:
        "Trace LLC-owned properties back to decision makers, managers, and usable contact paths.",
      features: [
        "LLC/entity resolution",
        "Manager and signer matching",
        "Business-to-person tracing",
      ],
    },
    {
      name: "DNC Scrubbing",
      eyebrow: "Compliance add-on",
      credits: "5 credits",
      unit: "per number",
      description:
        "Scrub phone numbers against DNC and litigation risk before your team starts dialing.",
      features: ["DNC suppression", "Litigation flags", "Dialer-ready output"],
    },
  ];
}

function CreditPill({ credits, unit, variant = "default" }) {
  const styles = {
    default: "bg-bg border border-white/8 text-text",
    dark: "bg-white/10 border border-white/10 text-white",
    brand: "bg-main-900/50 border border-white/10 text-white",
  };
  return (
    <div
      className={`inline-flex items-baseline gap-2 rounded-full px-4 py-2 text-sm font-semibold ${styles[variant]}`}
    >
      <span>{credits}</span>
      <span className="text-xs font-medium opacity-50">{unit}</span>
    </div>
  );
}

function ModeRates({ modes, unit }) {
  return (
    <div className="min-w-44 rounded-2xl bg-bg px-4 py-3 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]">
      {modes.map((m, i) => (
        <div
          key={m.name}
          className={`flex items-baseline justify-between gap-3 ${
            i > 0 ? "mt-1.5 pt-1.5 border-t border-white/6" : ""
          }`}
        >
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-text-muted">
            {m.name}
          </span>
          <span className="font-mono text-sm font-semibold text-text">
            {m.rate} <span className="text-text-muted">{m.rate === 1 ? "cr." : "cr."}</span>
          </span>
        </div>
      ))}
      <p className="mt-2 text-[10px] text-text-muted">{unit}</p>
    </div>
  );
}

export function Services() {
  const { traceRates } = useLoaderData();
  const SERVICES = buildServices(traceRates);

  return (
    <section
      className="relative overflow-hidden bg-bg-elevated px-6 py-32 sm:px-8 sm:py-40"
    >
      {/* Hairline edges */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/10 to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-linear-to-r from-transparent via-white/8 to-transparent"
      />

      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/4 top-1/2 h-110 w-140 -translate-y-1/2 rounded-full bg-main-700/10 blur-[140px]"
      />

      <div id="services" className="relative mx-auto max-w-360 scroll-mt-24">
        {/* Header — left-heavy like the reference */}
        <div className="mb-14 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <div className="max-w-2xl">
            <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.28em] text-main-300">
              Services
            </p>
            <h2 className="text-4xl font-bold tracking-[-0.03em] text-text sm:text-5xl md:text-6xl">
              Three products,
              <br />
              one credit system.
            </h2>
          </div>
          <p className="max-w-sm text-base leading-7 text-text-soft lg:text-right">
            Skip tracing, LLC resolution, and compliance scrubbing — all drawing
            from the same credit pool.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid gap-4 lg:grid-cols-12">

          {/* ── Skip Tracing — col-span-7, featured light card ── */}
          <article className="col-span-12 flex flex-col overflow-hidden rounded-3xl bg-card-2 p-8 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08),inset_0_1px_0_0_rgba(255,255,255,0.07)] lg:col-span-7">
            <div className="mb-10 flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-main-300">
                  {SERVICES[0].tag}
                </p>
                <h3 className="mt-3 text-5xl font-bold tracking-[-0.04em] text-text">
                  {SERVICES[0].name}
                </h3>
              </div>
              <ModeRates modes={SERVICES[0].modes} unit={SERVICES[0].unit} />
            </div>

            <p className="max-w-xl text-lg leading-8 text-text-soft">
              {SERVICES[0].description}
            </p>

            <div className="mt-10 grid gap-3 sm:grid-cols-3">
              {SERVICES[0].features.map((f) => (
                <div
                  key={f}
                  className="rounded-2xl bg-bg p-4 text-sm font-medium text-text-soft shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]"
                >
                  {f}
                </div>
              ))}
            </div>
          </article>

          {/* ── LLC Skip Tracing — col-span-5, dark card ── */}
          <article className="relative col-span-12 flex flex-col overflow-hidden rounded-3xl bg-bg p-8 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)] lg:col-span-5">
            {/* Corner glow */}
            <div
              aria-hidden
              className="pointer-events-none absolute -right-8 -top-8 h-48 w-48 rounded-full bg-main-500/20 blur-3xl"
            />

            <div className="relative mb-10 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-text-muted">
                  {SERVICES[1].eyebrow}
                </p>
                <h3 className="mt-3 text-4xl font-bold tracking-[-0.04em] text-text">
                  {SERVICES[1].name}
                </h3>
              </div>
              <CreditPill
                credits={SERVICES[1].credits}
                unit={SERVICES[1].unit}
                variant="dark"
              />
            </div>

            <p className="relative text-base leading-7 text-text-soft">
              {SERVICES[1].description}
            </p>

            <div className="relative mt-10 rounded-2xl border border-white/8 bg-white/4 p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-text-muted">
                Best for
              </p>
              <p className="mt-2 text-2xl font-bold tracking-[-0.03em] text-text">
                LLC-owned deals
              </p>
            </div>
          </article>

          {/* ── DNC Scrubbing — col-span-12, brand accent card ── */}
          <article className="relative col-span-12 overflow-hidden rounded-3xl bg-main-500 p-8 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.18)]">
            <div className="relative grid gap-8 lg:grid-cols-[0.55fr_1fr_0.6fr] lg:items-center">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50">
                  {SERVICES[2].eyebrow}
                </p>
                <h3 className="mt-3 text-4xl font-bold tracking-[-0.04em] text-white">
                  {SERVICES[2].name}
                </h3>
              </div>

              <p className="text-xl leading-8 text-white/75">
                {SERVICES[2].description}
              </p>

              <div className="rounded-2xl bg-white/15 p-5 backdrop-blur-sm shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]">
                <CreditPill
                  credits={SERVICES[2].credits}
                  unit={SERVICES[2].unit}
                  variant="brand"
                />
                <div className="mt-5 grid gap-2 text-sm font-medium text-white/75">
                  {SERVICES[2].features.map((f) => (
                    <span key={f}>{f}</span>
                  ))}
                </div>
              </div>
            </div>
          </article>

        </div>
      </div>
    </section>
  );
}
